import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type {
  AdminPartner,
  PartnerTier,
  RequestStatus,
} from "@tih/shared";
import {
  ApiError,
  adminCreatePartner,
  adminDeletePartner,
  adminFetchPartners,
  adminUpdatePartner,
} from "../../api/client";

const TIER_LABEL: Record<PartnerTier, string> = {
  monthly: "Monthly partner",
  one_night: "One-night sponsor",
  recording: "Recording gift",
};

const TABS: { key: RequestStatus | "ALL"; label: string }[] = [
  { key: "NEW", label: "New" },
  { key: "IN_PROGRESS", label: "In progress" },
  { key: "DONE", label: "Done" },
  { key: "ALL", label: "All" },
];

const EMPTY_FORM = {
  tier: "monthly" as PartnerTier,
  name: "",
  email: "",
  amountUgx: "",
  status: "NEW" as RequestStatus,
  notes: "",
};

export function AdminPartnersPage() {
  const formRef = useRef<HTMLElement>(null);
  const [tab, setTab] = useState<RequestStatus | "ALL">("NEW");
  const [rows, setRows] = useState<AdminPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const showForm = creatingNew || editingId != null;

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setRows(await adminFetchPartners(tab === "ALL" ? undefined : tab));
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const total = useMemo(
    () => rows.reduce((sum, r) => sum + r.amountUgx, 0),
    [rows],
  );

  function resetForm() {
    setCreatingNew(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
  }

  function startCreate() {
    resetForm();
    setCreatingNew(true);
    setSuccess(null);
    setError(null);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function startEdit(row: AdminPartner) {
    setCreatingNew(false);
    setEditingId(row.id);
    setForm({
      tier: row.tier,
      name: row.name,
      email: row.email,
      amountUgx: String(row.amountUgx),
      status: row.status,
      notes: row.notes ?? "",
    });
    setSuccess(null);
    setError(null);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);

    const amount = Number.parseInt(form.amountUgx.replace(/\D/g, ""), 10);
    if (!form.name.trim()) {
      setError("Name is required");
      setBusy(false);
      return;
    }
    if (!form.email.trim()) {
      setError("Email is required");
      setBusy(false);
      return;
    }
    if (!Number.isFinite(amount) || amount < 1000) {
      setError("Amount must be at least UGX 1,000");
      setBusy(false);
      return;
    }

    try {
      if (editingId != null) {
        await adminUpdatePartner(editingId, {
          tier: form.tier,
          name: form.name.trim(),
          email: form.email.trim(),
          amountUgx: amount,
          status: form.status,
          notes: form.notes.trim() || null,
        });
        setSuccess("Partner record updated.");
      } else {
        await adminCreatePartner({
          tier: form.tier,
          name: form.name.trim(),
          email: form.email.trim(),
          amountUgx: amount,
        });
        setSuccess("Partner record added.");
        resetForm();
      }
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    if (!confirm("Delete this partner record?")) return;
    setBusy(true);
    setError(null);
    try {
      await adminDeletePartner(id);
      if (editingId === id) resetForm();
      setSuccess("Partner record deleted.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete");
    } finally {
      setBusy(false);
    }
  }

  async function quickStatus(id: number, status: RequestStatus) {
    setBusy(true);
    setError(null);
    try {
      await adminUpdatePartner(id, { status });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update status");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header admin-page-header-row">
        <div>
          <h1>Partners</h1>
          <p>
            Track pledges from the public partner form and record offline gifts.
            {rows.length > 0 && (
              <>
                {" "}
                {rows.length} in this view · UGX {total.toLocaleString()}
              </>
            )}
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={startCreate}>
          + Record partner
        </button>
      </header>

      <div className="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={tab === t.key ? "active" : ""}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12, marginBottom: 16 }}>
          {success}
        </div>
      )}

      {loading ? (
        <div style={{ opacity: 0.5 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ opacity: 0.6, marginBottom: 24 }}>
          {tab === "ALL"
            ? "No partner records yet — click + Record partner to add one."
            : `No ${tab === "NEW" ? "new" : tab === "IN_PROGRESS" ? "in-progress" : "completed"} partners.`}
        </div>
      ) : (
        <ul className="admin-cards" style={{ marginBottom: 24 }}>
          {rows.map((r) => (
            <li key={r.id} className="admin-card">
              <div className="admin-card-meta">
                <strong>{r.name}</strong>
                <a href={`mailto:${r.email}`}>{r.email}</a>
                <span>· {TIER_LABEL[r.tier]}</span>
                <span>· UGX {r.amountUgx.toLocaleString()}</span>
                <span>· {new Date(r.createdAt).toLocaleString()}</span>
                <span className={`status-pill status-${r.status.toLowerCase()}`}>
                  {r.status.replace("_", " ")}
                </span>
              </div>
              {r.notes && (
                <p style={{ whiteSpace: "pre-wrap", margin: "12px 0 0" }}>{r.notes}</p>
              )}
              <div className="admin-card-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => startEdit(r)}
                >
                  Edit
                </button>
                <a href={`mailto:${r.email}`} className="btn btn-ghost">
                  Email
                </a>
                <select
                  value={r.status}
                  disabled={busy}
                  onChange={(e) => void quickStatus(r.id, e.target.value as RequestStatus)}
                  className={`status-select status-${r.status.toLowerCase()}`}
                  aria-label={`Status for ${r.name}`}
                >
                  <option value="NEW">New</option>
                  <option value="IN_PROGRESS">In progress</option>
                  <option value="DONE">Done</option>
                </select>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={busy}
                  onClick={() => void remove(r.id)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <section ref={formRef} className="admin-card">
          <h2 className="admin-card-title">
            {editingId != null ? "Edit partner" : "Record partner"}
          </h2>
          <p className="admin-card-foot" style={{ marginTop: 0, marginBottom: 16 }}>
            {editingId != null
              ? "Update pledge details, internal notes, or workflow status."
              : "Add a partner pledge received offline (cash, bank transfer, etc.)."}
          </p>
          <form onSubmit={(e) => void onSubmit(e)} className="admin-blog-form">
            <div className="admin-blog-form-fields">
              <label>
                <span>Tier</span>
                <select
                  value={form.tier}
                  disabled={busy}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, tier: e.target.value as PartnerTier }))
                  }
                >
                  <option value="monthly">Monthly partner</option>
                  <option value="one_night">One-night sponsor</option>
                  <option value="recording">Recording gift</option>
                </select>
              </label>
              <label>
                <span>Name</span>
                <input
                  value={form.name}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  type="email"
                  value={form.email}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
              </label>
              <label>
                <span>Amount (UGX)</span>
                <input
                  inputMode="numeric"
                  value={form.amountUgx}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, amountUgx: e.target.value }))}
                  placeholder="50000"
                  required
                />
              </label>
              {editingId != null && (
                <>
                  <label>
                    <span>Status</span>
                    <select
                      value={form.status}
                      disabled={busy}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          status: e.target.value as RequestStatus,
                        }))
                      }
                    >
                      <option value="NEW">New</option>
                      <option value="IN_PROGRESS">In progress</option>
                      <option value="DONE">Done</option>
                    </select>
                  </label>
                  <label>
                    <span>Internal notes</span>
                    <textarea
                      rows={3}
                      value={form.notes}
                      disabled={busy}
                      onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                      placeholder="Payment reference, thank-you sent, etc."
                    />
                  </label>
                </>
              )}
            </div>
            <div className="admin-blog-form-actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Saving…" : editingId != null ? "Save changes" : "Add partner"}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => {
                  resetForm();
                  setSuccess(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
